import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchSettings } from '../api';
import AccountSection from './settings/AccountSection';
import GeneralSection from './settings/GeneralSection';
import AppearanceSection from './settings/AppearanceSection';
import DeveloperSection from './settings/DeveloperSection';

const APP_VERSION = require('../../package.json').version;

const CATEGORIES = [
  {
    id: 'account',
    label: 'Konto',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
  {
    id: 'general',
    label: 'Allgemein',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    ),
  },
  {
    id: 'appearance',
    label: 'Darstellung',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
      </svg>
    ),
  },
  {
    id: 'developer',
    label: 'Entwickler',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
      </svg>
    ),
  },
];

function SettingsModal({ open, onClose, activeNote, onReset, onLicenseChange }) {
  const [settings, setSettings] = useState(null);
  const [message, setMessage] = useState(null);
  const [activeCategory, setActiveCategory] = useState('account');
  const [licenseStatus, setLicenseStatus] = useState(null);
  const overlayRef = useRef(null);
  const modalRef = useRef(null);

  // Lizenzstatus laden (nur in Electron)
  const refreshLicense = useCallback(async () => {
    if (!window.electronAPI?.license) return;
    try {
      const status = await window.electronAPI.license.getStatus();
      setLicenseStatus(status);
      onLicenseChange?.(status);
    } catch (err) {
      console.error('[License] Status-Abfrage fehlgeschlagen:', err);
    }
  }, [onLicenseChange]);

  // Focus-Trap: Tab/Shift+Tab bleibt innerhalb des Modals
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        const modal = modalRef.current;
        if (!modal) return;
        const focusable = modal.querySelectorAll('input, button, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Body-Scroll blockieren wenn Modal offen
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchSettings().then(data => {
        setSettings(data);
        setMessage(null);
      });
      refreshLicense();
    }
  }, [open, refreshLicense]);

  if (!open || !settings) return null;

  return (
    <div className="settings-overlay" ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}>
      <div className="settings-modal" ref={modalRef}>
        <div className="settings-header">
          <h2 className="settings-title">Einstellungen</h2>
          <button className="settings-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="settings-layout">
          {/* Sidebar */}
          <nav className="settings-sidebar">
            <div className="settings-sidebar-nav">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  className={`settings-sidebar-item${activeCategory === cat.id ? ' active' : ''}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.icon}
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
            <div className="settings-sidebar-version">v{APP_VERSION}</div>
          </nav>

          {/* Content */}
          <div className="settings-content">
            {activeCategory === 'account' && (
              <AccountSection
                settings={settings}
                setSettings={setSettings}
                setMessage={setMessage}
                licenseStatus={licenseStatus}
                onLicenseChange={refreshLicense}
              />
            )}
            {activeCategory === 'general' && (
              <GeneralSection
                settings={settings}
                setSettings={setSettings}
                setMessage={setMessage}
              />
            )}
            {activeCategory === 'appearance' && (
              <AppearanceSection />
            )}
            {activeCategory === 'developer' && (
              <DeveloperSection
                settings={settings}
                setSettings={setSettings}
                setMessage={setMessage}
                activeNote={activeNote}
                onReset={onReset}
              />
            )}

            {/* Status Message */}
            {message && (
              <div className={`settings-message ${message.type}`}>
                {message.text}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
